import { NavLink, Navigate, Outlet } from 'react-router-dom'
import { useMe } from '@/api/hooks'
import { LoadingState } from '@/components/ui/Spinner'
import { cn } from '@/lib/cn'

// The admin sections that exist in the app today. Order is the tab order.
const NAV: { to: string; label: string }[] = [
  { to: '/admin/tracks', label: 'Tracks' },
  { to: '/admin/segments', label: 'Segments' },
  { to: '/admin/audiences', label: 'Audiences' },
  { to: '/admin/badges', label: 'Badges & XP' },
  { to: '/admin/knowledgebase', label: 'Knowledgebase' },
  { to: '/admin/whats-new', label: "What's New" },
  { to: '/admin/hubspot', label: 'HubSpot' },
]

// Owner-only (super_admin) tabs — managing people + the audit trail. Appended
// after NAV and shown only when `me.canManageTeam` is true (see below); the
// pages themselves also route-guard on the same flag.
const OWNER_NAV: { to: string; label: string }[] = [
  { to: '/admin/team', label: 'Team' },
  { to: '/admin/audit-log', label: 'Audit log' },
]

/**
 * Admin CMS chrome + access gate (pass-2): lives inside the main app shell
 * (left sidebar), so this is just the "ADMIN CMS" eyebrow + a horizontal tab
 * bar (brand-filled active pill) above the active page. Only content managers
 * (super_admin / rep_admin) may enter; everyone else is redirected to the
 * library. The server enforces this on every write — this is the UX layer.
 */
export function AdminLayout() {
  const { data: me, isPending } = useMe()

  if (isPending) return <LoadingState label="Loading…" />
  if (!me?.canManageContent) return <Navigate to="/" replace />

  // The Owner-only tabs appear only for a Super Admin (canManageTeam); the rest
  // of the panel stays open to any content manager (Admin).
  const tabs = [...NAV, ...(me.canManageTeam ? OWNER_NAV : [])]

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <p className="text-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
          Admin CMS
        </p>
        <nav className="flex flex-wrap gap-2" aria-label="Admin sections">
          {tabs.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'text-body-sm inline-flex h-9 items-center rounded-full px-3.5 font-semibold transition-colors duration-150 ease-out',
                  isActive
                    ? 'bg-brand text-white shadow-[var(--shadow-sm)]'
                    : 'text-text-secondary hover:bg-subtle hover:text-text-primary',
                )
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </div>
      {/* Plain div, not <main>: AppShell already provides the #main-content
          <main> landmark this renders inside — nesting a second would duplicate
          the landmark (WCAG landmark uniqueness). */}
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  )
}
